import { useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useLocationSitemapPageData } from '#src/app/pages/local/sitemap/pageContext';
import { useLocale } from '#src/app/shared/hooks/l10n';
import { useQueryParamValue, useUpdateQueryString } from '#src/app/shared/hooks/location';

type Filter = {
	name: string;
	selected: boolean;
	value: string;
};

type Result = ModelHookResult<
	{
		hasSelectedFilters: boolean;
		visibleFilters: Filter[];
		shouldShowToggle: boolean;
		showFullList: boolean;
	},
	{
		updateFilter: (name: string) => void;
		clearFilters: () => void;
		toggleList: () => void;
	}
>;

export function useStateCodeFilter(): Result {
	const [showFullList, setShowFullList] = useState(false);

	const filterQueryParam = useQueryParamValue('state_code') || '';
	const updateQueryParams = useUpdateQueryString();
	const trackEvent = useTracking();
	const locale = useLocale();
	const {
		data: { states },
		actions: { refreshData },
	} = useLocationSitemapPageData();

	const filters = [...states]
		.sort((a, b) => a.displayName.localeCompare(b.displayName, locale))
		.map(({ stateCode, displayName }) => ({
			name: displayName,
			selected: filterQueryParam === stateCode,
			value: stateCode,
		}));
	const hasSelectedFilters = filterQueryParam.length > 0;

	// Arbitrary number to show initial list of filters to hide the
	// rest behind a "Show more" toggle
	const maxVisibleFilters = 25;
	const visibleFilters = showFullList ? filters : filters.slice(0, maxVisibleFilters);

	const updateFilter = (value: string) => {
		void trackEvent('location_sitemap_filters_click');
		updateQueryParams({ state_code: value }, { replace: true });
		refreshData();
	};

	const clearFilters = () => {
		void trackEvent('location_sitemap_clear_filters_click');
		updateQueryParams({ state_code: undefined }, { replace: true });
		refreshData();
	};

	const toggleList = () => {
		setShowFullList((prev) => !prev);
	};

	return {
		data: {
			hasSelectedFilters,
			visibleFilters,
			shouldShowToggle: filters.length > maxVisibleFilters,
			showFullList,
		},
		actions: {
			updateFilter,
			clearFilters,
			toggleList,
		},
	};
}
